/**
 * Decorative brand-wave backdrop: layered, translucent corporate purple/pink
 * ribbons with fine white contour lines, drawn as ONE composition.
 *
 * - `variant="app"` (default): mounted once by the AppShell as a fixed,
 *   viewport-sized layer behind BOTH the sidebar and the content column, so a
 *   ribbon that starts under the (translucent) sidebar continues unbroken
 *   into the dashboard and stays in place while the page scrolls.
 * - `variant="hero"`: the same shapes at full strength for the sign-in page.
 *
 * Every colour and opacity comes from the `--flow-*` tokens in tokens.css,
 * which differ for Light and Dark. Aesthetic only: `aria-hidden`,
 * `pointer-events: none`, painted behind content, takes no layout space, and
 * static on purpose (nothing animates, so there is no motion to reduce).
 */
import { useId } from 'react';
import styles from './AmbientFlow.module.css';

type AmbientFlowProps = {
  variant?: 'app' | 'hero';
  className?: string;
};

export function AmbientFlow({ variant = 'app', className }: AmbientFlowProps) {
  // Gradient ids must be unique per document and safe inside url(#…).
  const uid = `af${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const id = (name: string) => `${uid}-${name}`;
  const url = (name: string) => `url(#${id(name)})`;

  return (
    <div
      className={[styles.flow, variant === 'hero' ? styles.hero : styles.app, className].filter(Boolean).join(' ')}
      aria-hidden="true"
      data-testid="ambient-flow"
      data-variant={variant}
    >
      <svg className={styles.svg} viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice" focusable="false">
        <defs>
          {/* Main ribbons: purple on the left warming to pink on the right. */}
          <linearGradient id={id('a')} x1="0" y1="0.5" x2="1" y2="0.5">
            <stop offset="0" className={styles.stop2} style={{ stopOpacity: 0.15 }} />
            <stop offset="0.45" className={styles.stop2} />
            <stop offset="1" className={styles.stop1} />
          </linearGradient>
          <linearGradient id={id('b')} x1="0" y1="0.5" x2="1" y2="0.5">
            <stop offset="0" className={styles.stop3} />
            <stop offset="0.55" className={styles.stop1} />
            <stop offset="1" className={styles.stop2} />
          </linearGradient>
          <linearGradient id={id('c')} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" className={styles.stop3} style={{ stopOpacity: 0 }} />
            <stop offset="0.5" className={styles.stop3} />
            <stop offset="1" className={styles.stop1} />
          </linearGradient>
          {/* Upper drape behind the page header. */}
          <linearGradient id={id('u')} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" className={styles.stop3} style={{ stopOpacity: 0 }} />
            <stop offset="0.5" className={styles.stop3} />
            <stop offset="1" className={styles.stop1} />
          </linearGradient>
          <radialGradient id={id('g1')} cx="0.8" cy="0.12" r="0.55">
            <stop offset="0" className={styles.glow} />
            <stop offset="1" className={styles.glow} style={{ stopOpacity: 0 }} />
          </radialGradient>
          <radialGradient id={id('g2')} cx="0.08" cy="0.95" r="0.5">
            <stop offset="0" className={styles.glow} />
            <stop offset="1" className={styles.glow} style={{ stopOpacity: 0 }} />
          </radialGradient>
          <linearGradient id={id('haze')} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" className={styles.haze} />
            <stop offset="0.14" className={styles.haze} style={{ stopOpacity: 0 }} />
          </linearGradient>
        </defs>

        <rect width="1600" height="900" fill={url('g1')} />
        <rect width="1600" height="900" fill={url('g2')} />

        {variant === 'app' ? (
          <>
            {/* Upper drape: two soft folds sweeping from the top centre to the right
                edge, behind the page header. */}
            <path d="M260 0C520 90 800 150 1060 120C1300 92 1480 20 1600 0Z" fill={url('u')} />
            <path
              d="M420 0C640 160 900 230 1140 200C1340 175 1500 110 1600 60V130C1490 190 1330 260 1120 270C870 282 620 190 420 0Z"
              fill={url('u')}
            />
            <path d="M420 0C640 160 900 230 1140 200C1340 175 1500 110 1600 60" className={styles.edge} />
            <path d="M330 0C580 120 840 190 1100 165C1320 144 1490 70 1600 20" className={styles.edgeFine} />
          </>
        ) : (
          <>
            {/* Sign-in: two broad ribbons climbing from the lower left to the top right. */}
            <path
              d="M0 700C400 640 700 760 1000 620C1250 500 1420 220 1600 100V330C1440 420 1300 640 1060 760C760 900 380 820 0 900Z"
              fill={url('a')}
            />
            <path
              d="M300 900C700 820 1000 700 1200 520C1360 380 1480 260 1600 230V420C1480 450 1380 560 1240 680C1060 840 800 900 600 900Z"
              fill={url('b')}
            />
            <path d="M0 700C400 640 700 760 1000 620C1250 500 1420 220 1600 100" className={styles.edge} />
            <path d="M300 900C700 820 1000 700 1200 520C1360 380 1480 260 1600 230" className={styles.edge} />
            <path d="M200 900C560 830 880 720 1110 560C1300 430 1450 200 1600 40" className={styles.edgeFine} />
            <path d="M0 820C380 760 720 860 1040 700C1280 580 1440 330 1600 180" className={styles.edgeFine} />
          </>
        )}

        {variant === 'app' ? (
          <>
            {/* Lower sweep: three overlapping ribbons rising from the bottom left to the right. */}
            <path
              d="M0 640C260 600 520 720 820 700C1120 680 1360 520 1600 380V560C1380 660 1120 800 820 830C520 860 260 760 0 800Z"
              fill={url('a')}
            />
            <path
              d="M0 700C280 660 540 780 860 760C1160 740 1390 600 1600 470V505C1390 630 1160 775 860 795C540 815 280 700 0 735Z"
              fill={url('c')}
            />
            <path d="M0 770C300 730 560 850 900 830C1200 810 1420 690 1600 610V900H0Z" fill={url('b')} />

            {/* Fine white contour lines following the ribbon edges. */}
            <path d="M0 640C260 600 520 720 820 700C1120 680 1360 520 1600 380" className={styles.edge} />
            <path d="M0 735C280 700 540 815 860 795C1160 775 1390 630 1600 505" className={styles.edgeFine} />
            <path d="M0 770C300 730 560 850 900 830C1200 810 1420 690 1600 610" className={styles.edge} />
            <path d="M0 680C300 640 560 750 880 735C1180 720 1400 570 1600 440" className={styles.edgeFine} />
            <path d="M120 900C420 800 760 860 1060 790C1300 735 1470 640 1600 560" className={styles.edgeFine} />
          </>
        ) : (
          <>
            {/* Sign-in: a low, gentle wave in the lower left that stays below the copy. */}
            <path d="M0 735C240 705 470 770 720 900H0Z" fill={url('c')} />
            <path d="M0 800C220 785 420 830 560 900H0Z" fill={url('a')} />
            <path d="M0 735C240 705 470 770 720 900" className={styles.edge} />
            <path d="M0 770C230 745 440 800 640 900" className={styles.edgeFine} />
            <path d="M0 800C220 785 420 830 560 900" className={styles.edgeFine} />
          </>
        )}

        {/* Lift the very top so the header text always sits on a calm field. */}
        <rect width="1600" height="900" fill={url('haze')} />
      </svg>
    </div>
  );
}
